import { TrendingUp } from "lucide-react";
import { Comparacao, Delta } from "./delta";
import { StatusChip } from "./status-chip";
import { fmtMoedaBR, fmtDiaCurto } from "@/lib/formato-mercado";
import { useAuth } from "@/lib/auth-context";
import { useCotacaoHoje, useMinhasVsPraca, usePracaDaOrg } from "@/hooks/use-cotacoes";
import { labelReferencial } from "@shared/schema";
import type { ComparacaoDTO } from "@shared/mercado-cotacoes";

/**
 * O quadro da praça: preço se LÊ em lista, nunca em gráfico. Sem sparkline, sem
 * linha, sem eixo — decisão fechada com o Hugo (07–08/09).
 *
 * Desktop: 4 colunas por linha (produto · preço · variação na semana · você).
 * Celular: 2 linhas por produto — produto/preço em cima, você/variação embaixo.
 * **Sem rodapé.**
 *
 * Os dados chegam no PR 2 (Cotações). Este componente já nasce com o contrato
 * para que a home só troque o placeholder pelo quadro real.
 */
export interface LinhaQuadroPraca {
  /** "Prata 1ª", "Nanica". */
  label: string;
  precoKg: number;
  /** Variação contra a semana anterior; null sem base. */
  deltaSemana: number | null;
  /** Média ponderada das suas vendas no mês; null sem venda. */
  meuPreco: number | null;
  /**
   * A comparação com a praça, já na forma que o referencial permite (% ou
   * R$/kg de diferença). `null` sem venda. Ver `compararComPraca`.
   */
  deltaMeu: ComparacaoDTO | null;
}

export interface QuadroPracaProps {
  /** "Praça Irecê". */
  praca: string;
  /**
   * PRODUTOR | VENDA — o que a praça divulgou.
   *
   * ⚠️ O chip não é enfeite: o mesmo R$ 1,60 quer dizer "é isso que você
   * recebe" numa placa ao produtor e "é isso que a caixa embalada custa" numa
   * placa de venda. Sem a etiqueta, o produtor lê o número errado.
   */
  referencial: string;
  linhas: LinhaQuadroPraca[];
  /** Já formatado: "seg 07/09 07:10 · Rizoma". */
  atualizadoEm: string;
  /**
   * Dias desde o vencimento da cotação (PR 3). 0 = fresca.
   *
   * ⚠️ Vencida NÃO esvazia o quadro: o preço continua ali, com um chip ao lado.
   * A última cotação publicada é sempre a válida (regra do Hugo, 11/09) — sumir
   * com ela criaria um buraco justamente onde o produtor precisa de um número.
   */
  diasVencida?: number;
  onHistorico?: () => void;
  testId?: string;
}

export function QuadroPraca({ praca, referencial, linhas, atualizadoEm, diasVencida, onHistorico, testId }: QuadroPracaProps) {
  return (
    <section className="rounded-lg border bg-card shadow-xs overflow-hidden" data-testid={testId ?? "quadro-praca"}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
        <p className="text-sm font-semibold flex items-center gap-2">
          {praca} · hoje
          {/* A etiqueta do referencial saiu daqui: ela agora vai SOB CADA PREÇO,
              que é onde a leitura errada acontece. No cabeçalho ela ficava a
              quatro linhas de distância do número que qualifica. */}
          {!!diasVencida && diasVencida > 0 && (
            <StatusChip kind="warn" testId="chip-cotacao-vencida">
              vencida · há {diasVencida} {diasVencida === 1 ? "dia" : "dias"}
            </StatusChip>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          {atualizadoEm}
          {onHistorico && (
            <>
              {" · "}
              <button type="button" className="underline underline-offset-2 hover:text-foreground" onClick={onHistorico}>
                histórico ›
              </button>
            </>
          )}
        </p>
      </header>
      <ul className="divide-y">
        {/* DUAS LINHAS, e não quatro colunas.
            A comparação com a praça tem largura imprevisível: no modo VENDA ela
            é "−0,19 /kg para a placa", quase o dobro de "▲ 8,2%". Espremida numa
            coluna de grade ela quebrava o valor em duas linhas e o rótulo em
            três — em produção, no desktop a 1440. Com o preço grande sozinho à
            direita e a comparação numa segunda linha sob o nome do produto,
            sobra a largura do card inteiro e nada quebra em referencial nenhum.
            Uma estrutura só para os dois: o que muda é o CONTEÚDO. */}
        {linhas.map(l => (
          <li key={l.label} className="px-3 py-2.5 flex items-start justify-between gap-3" data-testid={`linha-quadro-${l.label}`}>
            <div className="min-w-0 space-y-0.5">
              <p className="text-sm font-medium truncate">{l.label}</p>
              <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1.5">
                {l.meuPreco != null ? (
                  <>
                    <span className="whitespace-nowrap">
                      você: <b className="font-mono tabular-nums text-foreground">{fmtMoedaBR(l.meuPreco)}</b>
                    </span>
                    <span aria-hidden="true">·</span>
                    <Comparacao valor={l.deltaMeu} testId={`comparacao-${l.label}`} />
                  </>
                ) : (
                  <span className="whitespace-nowrap">você: —</span>
                )}
              </p>
            </div>
            <div className="shrink-0 text-right space-y-0.5">
              <p className="font-mono text-xl md:text-[22px] font-semibold tabular-nums whitespace-nowrap leading-tight">
                {fmtMoedaBR(l.precoKg)}<span className="text-xs text-muted-foreground font-sans font-normal"> /kg</span>
              </p>
              {/* Sob o preço: a variação da PRAÇA na semana e a etiqueta do que
                  aquele número é. Os dois qualificam o preço de cima, não o
                  "você" da esquerda. */}
              <p className="flex items-center justify-end gap-1.5 text-[11.5px] text-muted-foreground">
                <Delta value={l.deltaSemana} className="bg-transparent px-0 py-0 text-[11.5px]" />
                <span aria-hidden="true">·</span>
                <span className="whitespace-nowrap">{labelReferencial(referencial)}</span>
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}


/**
 * O quadro sem nenhum preço publicado — honesto, nunca uma lista de traços.
 *
 * Mostra a ÚLTIMA data com publicação quando existe: "a praça não publicou hoje,
 * mas o último preço é de sexta" é uma informação útil; um card mudo que só diz
 * "sem cotação" faria o produtor achar que o módulo está quebrado.
 */
export function QuadroPracaSemCotacao({
  praca,
  ultimaData,
  compacto,
  onHistorico,
}: {
  /** "Praça Mirorós", ou `""` quando não há praça resolvida. */
  praca: string;
  /** ISO "AAAA-MM-DD" da última publicação, ou null se nunca houve nenhuma. */
  ultimaData: string | null;
  compacto?: boolean;
  onHistorico?: () => void;
}) {
  // Praça vazia e praça sem cotação são estados DIFERENTES, e o card diz qual
  // é: "Praça sem cotação publicada hoje" com o nome em branco leria como se a
  // praça não tivesse nome, quando o que não há é praça.
  const semPraca = !praca;
  return (
    <section
      className={`rounded-lg border border-dashed bg-card/60 flex flex-col items-center justify-center text-center gap-2 px-4 ${compacto ? "py-6" : "py-10"}`}
      data-testid="quadro-praca-sem-cotacao"
    >
      <TrendingUp className="h-6 w-6 text-muted-foreground" />
      <p className="text-sm font-medium">
        {semPraca ? "Nenhuma praça cadastrada ainda" : `${praca} sem cotação publicada hoje`}
      </p>
      <p className="text-xs text-muted-foreground max-w-sm">
        {semPraca
          ? "Assim que o Rizoma cadastrar a praça e publicar o preço do dia, ele aparece aqui."
          : ultimaData
            ? <>O último preço publicado é de <b className="text-foreground">{fmtDiaCurto(ultimaData)}</b>.{onHistorico && " Veja o histórico para o que veio antes."}</>
            : "Assim que o Rizoma publicar o preço do dia, ele aparece aqui — e ao lado de cada produto, quanto você vendeu em relação a ele."}
      </p>
      {!semPraca && ultimaData && onHistorico && (
        <button
          type="button"
          className="text-xs underline underline-offset-2 hover:text-foreground"
          onClick={onHistorico}
          data-testid="link-historico-sem-cotacao"
        >
          histórico ›
        </button>
      )}
    </section>
  );
}

/**
 * O quadro LIGADO nos dados: praça (a da org), preço do dia e as suas vendas.
 *
 * Existe para que a home e a tela de Cotações mostrem exatamente o MESMO quadro —
 * duas montagens divergiriam no primeiro campo novo, e o número do quadro é o que
 * o produtor leva para a negociação.
 *
 * A praça vem de `minhas-vs-praca` (que é quem sabe qual é a da org) e o preço da
 * rota pública. São duas queries porque são dois assuntos: preço é dado público,
 * "quanto EU vendi" é da fazenda — e é o react-query que evita o request repetido
 * quando as duas telas pedem o mesmo.
 */
/**
 * O quadro ligado aos dados.
 *
 * `pracaExplicita` é para o COMPRADOR: a praça dele não sai de `usePracaDaOrg`
 * (que responde por `minhas-vs-praca`, rota só do produtor), vem do seletor da
 * tela dele. Quando ela é passada, a consulta do produtor nem roda — e o quadro
 * mostra só o preço da praça, sem as colunas "você", que para quem COMPRA não
 * existem: ele não vende nada nessa placa.
 */
export function QuadroPracaConectado({ compacto, onHistorico, pracaExplicita }: {
  compacto?: boolean;
  onHistorico?: () => void;
  pracaExplicita?: string;
}) {
  const { user, modulesEnabled } = useAuth();
  const ativo = !pracaExplicita && user?.orgTipo !== "COMPRADOR" && modulesEnabled.indexOf("mercado") >= 0;
  const { data: vsPraca } = useMinhasVsPraca("mes", ativo);
  const daOrg = usePracaDaOrg();
  const praca = pracaExplicita ?? daOrg.praca;
  const nome = pracaExplicita ? undefined : daOrg.nome;
  // Com praça explícita não há o que resolver: quem espera é só o preço.
  const carregando = pracaExplicita ? false : daOrg.carregando;
  // Enquanto a praça não se resolve, a query do preço fica desabilitada e
  // `isLoading` é falso — por isso as duas condições, e não uma só.
  const { data: cotacao, isLoading: carregandoPreco } = useCotacaoHoje(praca);

  if (carregando || carregandoPreco) {
    return <div className={`rounded-lg border bg-card ${compacto ? "h-40" : "h-56"} animate-pulse`} data-testid="quadro-praca-carregando" />;
  }

  // Sem praça resolvida o card não inventa nome: "Praça" sozinho leria como o
  // nome da praça ser vazio, quando o que não há é praça cadastrada.
  const nomeResolvido = cotacao?.pracaNome ?? nome ?? null;
  const nomePraca = nomeResolvido ? `Praça ${nomeResolvido}` : "";

  if (!cotacao || cotacao.linhas.length === 0) {
    return (
      <QuadroPracaSemCotacao
        praca={nomePraca}
        ultimaData={cotacao?.ultimaData ?? null}
        compacto={compacto}
        onHistorico={onHistorico}
      />
    );
  }

  // `meuPreco` e o delta contra a praça já vêm calculados do servidor — a tela só
  // casa produto com produto pelo par (variedade, qualidade). Sem o comparativo
  // (org sem o módulo, ou a query falhou) o quadro mostra só o preço da praça.
  const meus = new Map((vsPraca?.produtos ?? []).map(p => [`${p.variedade}|${p.qualidade ?? ""}`, p]));

  return (
    <QuadroPraca
      praca={nomePraca}
      referencial={cotacao.referencial}
      atualizadoEm={`${fmtDiaCurto(cotacao.data)} · Rizoma`}
      onHistorico={onHistorico}
      diasVencida={cotacao.diasVencida}
      linhas={cotacao.linhas.map(l => {
        const meu = meus.get(`${l.variedade}|${l.qualidade ?? ""}`);
        return {
          label: l.label,
          precoKg: l.precoKg,
          deltaSemana: l.delta,
          meuPreco: meu?.meuPreco ?? null,
          deltaMeu: meu?.delta ?? null,
        };
      })}
    />
  );
}
